import type { ReactNode } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export interface Figure {
  label: string;
  /** Undefined while loading. */
  value: ReactNode | undefined;
  hint?: ReactNode;
  /** Red figure for things that should be zero. */
  bad?: boolean;
}

const COLS: Record<2 | 3 | 4 | 5 | 6, string> = {
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-2 lg:grid-cols-4',
  5: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5',
  6: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-6',
};

/**
 * A slim band of headline figures under a section's intro: eyebrow, the number in the display face, one hint.
 * Hairlines between cells come from the gap over a border-coloured background. Smaller than the Overview's
 * tiles on purpose; these give context, they are not the page.
 */
export function FigureBand({ figures, className, ariaLabel }: { figures: Figure[]; className?: string; ariaLabel?: string }) {
  const n = Math.min(Math.max(figures.length, 2), 6) as 2 | 3 | 4 | 5 | 6;
  return (
    <dl className={cn('bg-border grid gap-px border', COLS[n], className)} aria-label={ariaLabel}>
      {figures.map((f) => (
        <div key={f.label} className="bg-card min-w-0 px-4 py-3 sm:px-5">
          <dt className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">{f.label}</dt>
          <dd className={cn('font-display mt-1.5 text-2xl leading-none tracking-tight tabular-nums sm:text-[1.75rem]', f.bad && 'text-destructive')}>{f.value === undefined ? <Skeleton className="h-7 w-12" /> : f.value}</dd>
          {f.hint !== undefined && <dd className="text-muted-foreground mt-1.5 truncate text-xs">{f.hint}</dd>}
        </div>
      ))}
    </dl>
  );
}
